/**
 * HRTrash — Corbeille RH avec restore/purge (Vague 5).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, ArchiveRestore, Trash, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function HRTrash() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/hr/trash"); setRows(r.data.trash || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (col, id) => {
    try { await api.post(`/hr/trash/${col}/${id}/restore`); toast.success("Restauré"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purge = async (col, id) => {
    if (!window.confirm("Suppression DÉFINITIVE — action irréversible. Confirmer ?")) return;
    try { await api.delete(`/hr/trash/${col}/${id}/purge`); toast.success("Purgé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purgeOld = async () => {
    if (!window.confirm("Purger tous les items supprimés depuis > 30 jours (définitif) ?")) return;
    try { const r = await api.post("/hr/trash/purge-old"); toast.success(`${r.data.purged} items purgés`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const willPurge = rows.filter(r => r._will_purge).length;

  return (
    <div className="space-y-6" data-testid="hr-trash-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Corbeille RH
          </h1>
          <p className="text-sm text-slate-500">
            {rows.length} item{rows.length > 1 ? "s" : ""} · purge auto après 30 jours
            {willPurge > 0 && <span className="text-red-600 font-medium"> · {willPurge} à purger</span>}
          </p>
        </div>
        {canWrite && (
          <Button variant="outline" onClick={purgeOld} className="text-red-600 border-red-200"
            data-testid="btn-purge-old">
            <Trash className="w-4 h-4 mr-1.5" /> Purger les vieux
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="trash-empty">
              <Trash2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Corbeille vide.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Type</TableHead>
                <TableHead className="text-xs uppercase">Identifiant</TableHead>
                <TableHead className="text-xs uppercase">Supprimé le</TableHead>
                <TableHead className="text-xs uppercase text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r._collection + r.id} className="border-b" data-testid={`trash-row-${i}`}>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-50">{r._collection_label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-mono text-slate-500 text-xs">{r.id.slice(0, 8)}</div>
                      <div className="text-slate-700">
                        {r.first_name ? `${r.first_name} ${r.last_name || ""}`.trim() :
                          r.title || r.period || r.reason || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {r.deleted_at && new Date(r.deleted_at).toLocaleString("fr-FR")}
                      {r._will_purge && (
                        <Badge variant="outline" className="ml-2 bg-red-50 text-red-700 border-red-200 text-[10px]">
                          <AlertTriangle className="w-3 h-3 mr-1" /> À purger
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => restore(r._collection, r.id)}
                            className="h-7 text-xs" data-testid={`restore-${i}`}>
                            <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restaurer
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => purge(r._collection, r.id)}
                            className="h-7 text-red-600" data-testid={`purge-${i}`}>
                            <Trash className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
